import { semanticSearchStringAtom } from '@components/listings-search-bar/atoms';
import {
	type FacetFilterValue,
	type FilterStrategy,
	createFilterStrategy,
} from '@pages/VendorsSearch/containers/FilterPills/strategies/filter-strategy';
import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import type { FilterType, SearchFilters } from 'types/search';
import { reduxAtom } from '../../../../jotai/redux-atoms';
import {
	categoryCodeAtom,
	categoryIdAtom,
	sidebarCategoriesAtom,
} from '../../../../jotai/redux-select-atoms';

const dynamicMaxRangeAtom = selectAtom(reduxAtom, (state) => {
	const intervals = state.search?.priceRange?.intervals;
	if (!intervals?.length) return undefined;
	const last = intervals[intervals.length - 1];
	return last.range.to ?? last.range.from;
});

interface FilterOptions {
	id: string;
	name: string;
	/** Category code (e.g. 'PHOT'). Populated only for the cross-category filter. */
	code?: string;
	singular: {
		slug: string;
		term?: string;
	};
	plural: {
		slug: string;
		term?: string;
	};
}

export interface FilterConfig {
	id: string;
	type: FilterType;
	name: string;
	filterOptions: FilterOptions[];
	slug?: string;
	/** When true, the synthetic 'All' radio option is not prepended to single-select filter dropdowns. */
	hideAllOption?: boolean;
}

export interface FilterConfigs {
	[key: string]: FilterConfig;
}

export const CATEGORY_FILTER_ID = 'category';
export const DISTANCE_FILTER_ID = 'distance';

export interface FacetSingleSelectFilterValue {
	facetId: string;
}

export interface FacetMultiSelectFilterValue
	extends Array<{
		facetId: string;
		facetName?: string;
		facetValue?: string;
	}> {}

export interface PricingFilterValue {
	minPrice?: number;
	maxPrice?: number;
	vanitySlug?: string; // The original facet slug like 'affordable', 'luxury', etc.
}

export type FilterValue = {
	id: string;
	value:
		| FacetSingleSelectFilterValue
		| FacetMultiSelectFilterValue
		| PricingFilterValue
		| null;
};

export interface AppliedFilterPill {
	filterId: string;
	facetId?: string;
	name: string;
}

export interface SearchQueryParams {
	filters: string[];
	rangeFilters: {
		field: string;
		gte?: number;
		lte?: number;
	}[];
}

interface AppliedFiltersFormatted {
	id: string;
	name: string;
	value: string;
	categorySlug: string;
}

export const filterConfigsAtom = atom<FilterConfigs>({});

/**
 * Snapshot of filterConfigsAtom as set during server-side initialization.
 * Used by useCategoryFilterSync to reset to the base (universal-only) state
 * when 0 or 2+ categories are selected on the all-categories page.
 */
export const baseFilterConfigsAtom = atom<FilterConfigs | null>(null);

/**
 * Filter names that are shown on the all-categories page regardless of category selection.
 * Exported so Sidebar/atoms.ts can reuse the same set.
 */
export const UNIVERSAL_FILTER_NAMES = new Set([
	'Distance',
	'Best Of Weddings',
	'Business Attributes',
]);

/**
 * True only on the all-categories page. Set server-side in categoryFilters.ts
 * (serialized through the SSR hydration pipeline) and kept in sync on
 * client-side navigation by WrappedVendorsSearch.
 */
export const isAllCategoriesPageAtom = atom(false);

/**
 * Per-category LISTINGS_PRICING flag values, pre-evaluated during SSR.
 * Maps category code (e.g. 'REC') → boolean.
 */
export const listingsPricingByCategoryAtom = atom<Record<string, boolean>>({});

export const filterValuesAtom = atom<FilterValue[]>([]);

/**
 * Raw (unbuilt) filter summaries for every category, keyed by categoryId.
 * Seeded server-side in categoryFilters.ts from request.server.app.categories.slugToInfo,
 * which already holds full filter data for every category in memory (populated
 * once at server startup). Shipping all categories trades a larger SSR hydration
 * payload for eliminating extra server fetch/work — the data is already in memory
 * and readily available, versus only shipping the current page's category.
 * Lets useCategoryFilterSync look up any category's filters instantly on
 * first selection instead of calling categoryFiltersApi, since the data was
 * already available on the server for this request.
 */
export const categoryFilterSummariesAtom = atom<Record<string, SearchFilters>>(
	{},
);

/**
 * Set to `true` by the classifier category-sync hook just before it updates
 * `filterValuesAtom`, so that the `urlFilterParams` effect in FilterPills can
 * skip its `history.push` call (the classifier handles the URL update via
 * React Router's `history.replace`).
 */
export const classifierSyncAtom = atom(false);

/**
 * Stores the exact URL (pathname+search) that was just applied from browser
 * back/forward navigation. The FilterPills effect consumes this one-shot marker
 * to skip updateHistory exactly for that URL and then clears it.
 */
export const browserNavigationUrlAtom = atom<string | null>(null);

/**
 * Stores the category GUIDs that were last pre-selected by the backend
 * classifier (via `useClassifierCategorySync`). Null when no classifier sync
 * has occurred or after the classifier result is cleared.
 *
 * Used to derive `wasAutoSelectedByClassifierAtom` — true when the current
 * category filter selection matches what the classifier last set.
 */
export const classifierSelectedCategoryIdsAtom = atom<string[] | null>(null);

/** The semantic query that produced the current classifier-selected categories. */
export const classifierSearchStringAtom = atom<string | null>(null);

export interface ClassifierSsrSync {
	/**
	 * Idempotency key — same format as useClassifierCategorySync's `serialized`
	 * (see classifier-sync-helpers.ts's computeClassifierSyncKey). When it
	 * matches the key the client computes for the current classifier result,
	 * SSR already seeded filterValuesAtom/classifierSelectedCategoryIdsAtom
	 * with that result, so the client hook skips re-deriving/re-applying it.
	 */
	key: string;
	/** Pre-classifier category code(s), for the attribution event's `originalCategoryCode`. */
	originalCategoryCode: string | null;
}

/**
 * Set server-side (seedClassifierState.ts) when the backend classifier
 * already resolved categories for a location-only (`?q=`) landing during SSR
 * and filterValuesAtom/classifierSelectedCategoryIdsAtom were seeded with the
 * result — the same GS-802 pattern categoryFilters.ts uses for slug pages,
 * applied to the classifier's output. Never set for single-category slug
 * pages: GS-802's initializeFilters already seeds those directly, so there's
 * no client-side sync/flash to eliminate there.
 *
 * Read once by useClassifierCategorySync on mount: when its computed key
 * matches, the hook skips the filterValues mutation and history.replace it
 * would otherwise perform (the state is already correct and hydrated), only
 * suppressing the one extra search-fetch invocation the resulting Redux
 * category sync causes and firing the attribution event.
 */
export const classifierSsrSyncAtom = atom<ClassifierSsrSync | null>(null);

/**
 * Monotonically incremented, once, every time useClassifierCategorySync
 * performs a genuine classifier-driven sync (the same point it sets
 * skipNextFetchRef so the parent fetch-effect skips a re-fetch). Never
 * reset/decremented by anyone — this is a one-directional generation
 * counter, not a "pending" flag.
 *
 * Consumed by useVendorCategoryViewedTracking to detect the view
 * transition(s) immediately following a classifier sync, for which no
 * isFetching cycle will ever occur (skipNextFetchRef deliberately
 * suppresses the fetch), so its stale-data guard would otherwise block
 * tracking indefinitely for that transition.
 *
 * Deliberately NOT reused: classifierSyncAtom (read-and-reset by FilterPills
 * in the same commit, before this hook's sibling effect runs — see its own
 * doc comment above) and wasAutoSelectedByClassifierAtom (a persistent
 * derived value, not a one-shot pulse — see useVendorCategoryViewedTracking).
 */
export const classifierSyncGenerationAtom = atom<number>(0);

/**
 * True when the category filter has selected GUIDs but one or more GUIDs
 * cannot be resolved to a category code in the current filter config.
 *
 * Tracking can use this to distinguish:
 * - [] because nothing is selected, vs
 * - [] because selected GUID->code resolution failed.
 */
export const selectedCategoryCodesResolutionFailedAtom = atom<boolean>(
	(get) => {
		const filterConfigs = get(filterConfigsAtom);
		const categoryConfig = filterConfigs[CATEGORY_FILTER_ID];
		if (!categoryConfig) return false;

		const filterValues = get(filterValuesAtom);
		const categoryFilterValue = filterValues.find(
			(fv) => fv.id === CATEGORY_FILTER_ID,
		);
		if (!categoryFilterValue || !Array.isArray(categoryFilterValue.value)) {
			return false;
		}

		const selected = categoryFilterValue.value as Array<{ facetId: string }>;
		if (selected.length === 0) return false;

		return selected.some(
			({ facetId }) =>
				!categoryConfig.filterOptions.some((o) => o.id === facetId),
		);
	},
);

/**
 * True when the current category filter selection matches the last set of
 * categories pre-selected by the backend classifier.
 *
 * Note: because this is derived state, it can become `true` again if the user
 * later changes the selection back to the classifier-selected set.
 */
export const wasAutoSelectedByClassifierAtom = atom<boolean>((get) => {
	const classifierIds = get(classifierSelectedCategoryIdsAtom);
	if (!classifierIds || classifierIds.length === 0) return false;

	const filterValues = get(filterValuesAtom);
	const categoryFilterValue = filterValues.find(
		(fv) => fv.id === CATEGORY_FILTER_ID,
	);
	if (!categoryFilterValue || !Array.isArray(categoryFilterValue.value))
		return false;

	const selectedIds = (
		categoryFilterValue.value as Array<{ facetId: string }>
	).map((v) => v.facetId);

	if (selectedIds.length !== classifierIds.length) return false;

	const classifierSet = new Set(classifierIds);
	return selectedIds.every((id) => classifierSet.has(id));
});

export const shouldSkipCrossCategoryClassificationAtom = atom<boolean>(
	(get) => {
		const classifierSearchString = get(classifierSearchStringAtom);
		return (
			classifierSearchString !== null &&
			classifierSearchString === get(semanticSearchStringAtom)
		);
	},
);

export const appliedFiltersAtom = atom<FilterStrategy[]>((get) => {
	const categoryCode = get(categoryCodeAtom);
	const filterValues = get(filterValuesAtom);
	const filterConfigs = get(filterConfigsAtom);
	return filterValues
		.map(({ id, value }) => {
			const filterConfig = filterConfigs[id];
			if (!filterConfig || !value) {
				return null;
			}
			// Type assertion needed because value is the inner type, not FilterValue
			return createFilterStrategy(
				value as
					| FacetSingleSelectFilterValue
					| FacetMultiSelectFilterValue
					| PricingFilterValue,
				filterConfig,
				{
					categoryCode,
				},
			);
		})
		.filter((item): item is FilterStrategy => item !== null);
});

export const appliedFacetIdsAtom = atom<string[]>((get) => {
	const appliedFilters = get(appliedFiltersAtom);
	return appliedFilters.flatMap((filterStrategy) =>
		filterStrategy.getAppliedFacetIds(),
	);
});

/**
 * Facet IDs specifically for SEO text matching via seoFilter()
 * This includes vanity slug facet IDs for pricing filters
 */
export const seoFacetIdsAtom = atom<string[]>((get) => {
	const appliedFilters = get(appliedFiltersAtom);
	return appliedFilters.flatMap((filterStrategy) =>
		filterStrategy.getSeoFacetIds(),
	);
});

export const appliedFiltersCountAtom = atom<number>((get) => {
	const appliedFilters = get(appliedFiltersAtom);
	return appliedFilters.reduce((total, filterStrategy) => {
		return total + filterStrategy.calculateCount();
	}, 0);
});

export const selectedSidebarFiltersAtom = atom<string[]>([]);

export const appliedFiltersSidebarAtom = atom<FilterStrategy[]>((get) => {
	const sidebarCategories = get(sidebarCategoriesAtom);
	const appliedFilters = get(appliedFiltersAtom);

	return appliedFilters.filter((filterStrategy) => {
		const { config } = filterStrategy.getData();
		return sidebarCategories.includes(config?.name);
	});
});

export const appliedFilterPillsAtom = atom<AppliedFilterPill[]>((get) => {
	const appliedFilters = get(appliedFiltersAtom);
	const dynamicMaxRange = get(dynamicMaxRangeAtom);
	return appliedFilters.flatMap((filterStrategy) =>
		filterStrategy.getAppliedFilterPills(dynamicMaxRange),
	);
});

export const categoryIdFilterAtom = atom<string | null>((get) => {
	return get(categoryIdAtom);
});

/**
 * Returns true when the page should be noindexed due to cross-category filter state.
 *
 * A category filter value entry only exists in filterValuesAtom when ?category= query
 * params are present (set by queryParamsToFilterValues). When there is no category entry
 * the page is either a standard single-category URL (category comes from the route slug)
 * or the all-categories page (no category slug in the route). Only noindex the latter.
 *
 * noindex when:
 * - no ?category= query param AND isAllCategoriesPageAtom is true (all-categories page)
 * - exactly 0 categories explicitly selected via query param (filter was cleared)
 * - more than 1 category selected (multi-category page, non-canonical)
 */
export const shouldNoIndexForCategoryAtom = atom<boolean>((get) => {
	const filterConfigs = get(filterConfigsAtom);
	const isCrossCategoryActive = 'category' in filterConfigs;
	if (!isCrossCategoryActive) return false;

	const filterValues = get(filterValuesAtom);
	const selectedCategories = filterValues.find((fv) => fv.id === 'category');

	// No category query param → could be a standard single-category URL or the
	// all-categories page. Only noindex when this is the all-categories page.
	if (!selectedCategories) return get(isAllCategoriesPageAtom);

	const count = Array.isArray(selectedCategories.value)
		? selectedCategories.value.length
		: 0;
	return count !== 1;
});

/**
 * UI counterpart of shouldNoIndexForCategoryAtom.
 * Returns true when the cross-category filter is active and the user has
 * either no category selected or more than one category selected.
 * Unlike shouldNoIndexForCategoryAtom, treats a missing category filter entry
 * (no ?category= query param) as "all categories" → returns true.
 */
export const isMultiOrNoCategoryUIAtom = atom<boolean>((get) => {
	const filterConfigs = get(filterConfigsAtom);
	if (!(CATEGORY_FILTER_ID in filterConfigs)) return false;

	const filterValues = get(filterValuesAtom);
	const selected = filterValues.find((fv) => fv.id === CATEGORY_FILTER_ID);

	// No category param in cross-category mode → all categories shown
	if (!selected) return true;

	const count = Array.isArray(selected.value) ? selected.value.length : 0;
	return count !== 1;
});

export const isMultipleCategoriesSelectedAtom = atom<boolean>((get) => {
	const filterConfigs = get(filterConfigsAtom);
	if (!(CATEGORY_FILTER_ID in filterConfigs)) return false;

	const selected = get(filterValuesAtom).find(
		(filterValue) => filterValue.id === CATEGORY_FILTER_ID,
	);

	return Array.isArray(selected?.value) && selected.value.length > 1;
});

/**
 * Returns the category code implied by the current filter selection.
 *
 * When exactly 1 category is selected in the cross-category filter, returns
 * that category's code immediately from Jotai state (before Redux updates).
 * Otherwise returns `categoryCodeAtom` (the Redux-sourced value), ensuring
 * the transitional guard only applies in the specific case where the user
 * just selected a single category that differs from Redux's current value.
 *
 * Used by WrappedVendorsSearch Effect 2 to detect the transitional window
 * where the user has selected a category but Redux hasn't been updated yet
 * (because `setCategoryFilters` is async). While `effectiveCategoryCode !==
 * categoryCode`, Effect 2 should skip the search — the correct search will
 * fire once Redux catches up and both values agree.
 */
export const effectiveCategoryCodeAtom = atom<string>((get) => {
	const categoryCode = get(categoryCodeAtom) ?? '';
	const baseFilterConfigs = get(baseFilterConfigsAtom);

	// Only attempt to derive from filter selection if in cross-category mode
	if (!baseFilterConfigs || !(CATEGORY_FILTER_ID in baseFilterConfigs)) {
		return categoryCode;
	}

	const filterValues = get(filterValuesAtom);
	const selected = filterValues.find((fv) => fv.id === CATEGORY_FILTER_ID);

	// Only apply filter-derived code if exactly 1 category is selected
	if (
		!selected ||
		!Array.isArray(selected.value) ||
		selected.value.length !== 1
	) {
		return categoryCode;
	}

	const [selectedItem] = selected.value as Array<{ facetId: string }>;
	const option = baseFilterConfigs[CATEGORY_FILTER_ID].filterOptions.find(
		(opt) => opt.id === selectedItem.facetId,
	);

	// If we can extract a code from the selected filter, use it; otherwise fallback
	return option?.code ?? categoryCode;
});

/**
 * Returns the list of selected category codes for the current page.
 *
 * - Not in cross-category mode: `[categoryCode]` from Redux (single-element, backward-compatible)
 * - Cross-category mode, 0 categories selected: `[]` (all-categories page)
 * - Cross-category mode, 1+ categories selected: one code per selected category
 *
 * Use `.join(',')` when passing to tracking events; when the array is empty, send `null`
 * so all-categories pages don't send an empty string.
 */
export const selectedCategoryCodesAtom = atom<string[]>((get) => {
	const categoryCode = get(categoryCodeAtom) ?? '';
	const baseFilterConfigs = get(baseFilterConfigsAtom);

	// Not in cross-category mode — return single Redux code
	if (!baseFilterConfigs || !(CATEGORY_FILTER_ID in baseFilterConfigs)) {
		return categoryCode ? [categoryCode] : [];
	}

	const filterValues = get(filterValuesAtom);
	const selected = filterValues.find((fv) => fv.id === CATEGORY_FILTER_ID);

	// No category param or empty selection (all-categories page)
	if (
		!selected ||
		!Array.isArray(selected.value) ||
		selected.value.length === 0
	) {
		return [];
	}

	return (selected.value as Array<{ facetId: string }>).reduce<string[]>(
		(acc, item) => {
			const code = baseFilterConfigs[CATEGORY_FILTER_ID].filterOptions.find(
				(opt) => opt.id === item.facetId,
			)?.code;
			if (code) acc.push(code);
			return acc;
		},
		[],
	);
});

// Module-level memo cache: returns the same object reference when filter
// content hasn't changed, preventing Effect 2 from firing on every render.
// NOTE: Shared across all Jotai stores; test isolation requires explicit reset.
let searchQueryParamsMemo: { key: string; result: SearchQueryParams } | null =
	null;

/** @knipignore test-only — reset search params memo cache between tests to prevent cross-store pollution */
export const _resetSearchQueryParamsMemo = () => {
	searchQueryParamsMemo = null;
};

export const searchQueryParamsAtom = atom<SearchQueryParams>((get) => {
	const categoryId = get(categoryIdAtom);
	const appliedFilters = get(appliedFiltersAtom);
	const dynamicMaxRange = get(dynamicMaxRangeAtom);
	const filterConfigs = get(filterConfigsAtom);
	const isCrossCategoryActive = CATEGORY_FILTER_ID in filterConfigs;
	const initialFilters = isCrossCategoryActive ? [] : [categoryId];
	const result = appliedFilters.reduce(
		(acc, filterStrategy) => {
			const params = filterStrategy.getSearchQueryParams(dynamicMaxRange);

			if (params.filters) {
				acc.filters = acc.filters.concat(params.filters);
			}

			if (params.rangeFilters) {
				acc.rangeFilters = acc.rangeFilters.concat(params.rangeFilters);
			}
			return acc;
		},
		{ filters: initialFilters, rangeFilters: [] } as SearchQueryParams,
	);

	const key = JSON.stringify(result);
	if (searchQueryParamsMemo && searchQueryParamsMemo.key === key) {
		return searchQueryParamsMemo.result;
	}
	searchQueryParamsMemo = { key, result };
	return result;
});

const orderAppliedFiltersByCategory = (
	appliedFilters: FilterStrategy[],
	selectedSidebarFilters: string[],
) => {
	const ordered: FilterStrategy[] = [];
	const addedIds: string[] = [];

	if (selectedSidebarFilters.length === 0) {
		return appliedFilters;
	}

	for (const filterId of selectedSidebarFilters) {
		for (const appliedFiltersCategory of appliedFilters) {
			const appliedCategoryData = appliedFiltersCategory.getData();

			if (addedIds.includes(appliedCategoryData.config.id)) {
				continue;
			}

			if (filterId === 'price') {
				if (appliedCategoryData.config.id === 'starting-price') {
					ordered.push(appliedFiltersCategory);
					addedIds.push(appliedCategoryData.config.id);
					break;
				}
				continue;
			}

			if (appliedCategoryData.config.id === 'starting-price') {
				continue;
			}

			const facetsInCategory = Array.isArray(appliedCategoryData.value)
				? appliedCategoryData.value.flatMap((value) => value.facetId)
				: [appliedCategoryData.value.facetId];

			if (facetsInCategory.includes(filterId)) {
				ordered.push(appliedFiltersCategory);
				addedIds.push(appliedCategoryData.config.id);
				break;
			}
		}
	}

	for (const filterCategory of appliedFilters) {
		const filterCategoryData = filterCategory.getData();
		if (!addedIds.includes(filterCategoryData.config.id)) {
			ordered.push(filterCategory);
			addedIds.push(filterCategoryData.config.id);
		}
	}

	return ordered;
};

const toSortedParamPairs = (params: { key: string; value: string }[]) =>
	// Only needs a consistent order to align `a`/`b` for comparison, not a
	// linguistically correct one, so plain comparison avoids ICU collation overhead.
	[...params].sort((a, b) => {
		if (a.key !== b.key) {
			return a.key < b.key ? -1 : 1;
		}
		if (a.value === b.value) {
			return 0;
		}
		return a.value < b.value ? -1 : 1;
	});

const areUrlFilterParamsEqual = (
	a: { key: string; value: string }[],
	b: { key: string; value: string }[],
) => {
	if (a.length !== b.length) {
		return false;
	}
	// Order-insensitive: filterValues can arrive in different orders from
	// different writers (URL parse, sidebar, classifier sync) for the same
	// logical selection. Sorting (rather than a key->value Map) stays correct
	// even if duplicate keys are ever produced, instead of silently collapsing them.
	// Compares key/value fields directly rather than a joined string, so no
	// delimiter/encoding assumption about their contents is needed.
	const aSorted = toSortedParamPairs(a);
	const bSorted = toSortedParamPairs(b);
	return aSorted.every(
		(pair, index) =>
			pair.key === bSorted[index].key && pair.value === bSorted[index].value,
	);
};

/** @knipignore test-only — exercise duplicate-key handling directly, since no real filter strategy produces duplicates through urlFilterParamsAtom */
export const _areUrlFilterParamsEqual = areUrlFilterParamsEqual;

const urlFilterParamsComputeAtom = atom<{ key: string; value: string }[]>(
	(get) => {
		const appliedFilters = get(appliedFiltersAtom);
		const dynamicMaxRange = get(dynamicMaxRangeAtom);
		const selectedSidebarFilters = get(selectedSidebarFiltersAtom);

		const orderedAppliedFilters = orderAppliedFiltersByCategory(
			appliedFilters,
			[...selectedSidebarFilters].reverse(),
		);

		return orderedAppliedFilters.flatMap((filterStrategy) => {
			const queryParams = filterStrategy.getUrlQueryParams(dynamicMaxRange);
			return queryParams;
		});
	},
);

// selectAtom with an identity selector keeps the same array reference across
// recomputes with equal content, so effects keyed on this atom (e.g. the
// history-sync effect in FilterPills/index.tsx) don't fire spuriously.
export const urlFilterParamsAtom = selectAtom(
	urlFilterParamsComputeAtom,
	(v) => v,
	areUrlFilterParamsEqual,
);

export const appliedFacetsForImpressionsAtom = atom((get) => {
	const appliedFilters = get(appliedFiltersAtom);
	return appliedFilters.reduce<Record<string, string[]>>(
		(acc, appliedFilter) => {
			const facetValues = appliedFilter.getAppliedFacetValues();
			const id = appliedFilter.getData().config.id;
			if (facetValues.length) {
				acc[id] = facetValues;
			}
			return acc;
		},
		{},
	);
});

export const appliedFiltersFormattedAtom = atom((get) => {
	const appliedFilters = get(appliedFiltersAtom);
	const final = appliedFilters.map((filter) => {
		const data = filter.getData();
		if (filter.type === 'pricing') {
			const value = data.value as { minPrice: number; maxPrice: number };

			return (Object.keys(value) as (keyof typeof value)[]).map((key) => {
				return {
					id: data.config.id,
					name: data.config.name,
					value: value[key].toString(),
					categorySlug: key,
				} as AppliedFiltersFormatted;
			});
		}
		if (filter.type === 'facet-single-select') {
			const value = data.value as FacetFilterValue;
			return {
				id: value.facetId,
				name: value.facetName,
				value: value.facetValue,
				categorySlug: data.config.slug || '',
			} as AppliedFiltersFormatted;
		}
		if (filter.type === 'facet-multi-select') {
			const value = data.value as FacetFilterValue[];
			return value.map((facet: FacetFilterValue) => ({
				id: facet.facetId,
				name: facet.facetName,
				value: facet.facetValue,
				categorySlug: data.config.slug || '',
			}));
		}
		return [];
	});
	return final.flat();
});

export {
	stagedResultsCountAtom,
	stagedResultsCountLoadableAtom,
	updateStagedResultsCountAtom,
} from './atoms/staged-results-count-atom';
